import './UserProgram.scss'

import { useContext, useEffect, useState } from 'react'
import { Container, Row, Col, Button } from 'react-bootstrap'

import UserProgramContext from 'components/common/contexts/UserProgramContext'
import CourseContext from 'components/common/contexts/CourseContext'
import Loading from 'templates/Loading'
import UserProgramCourse from './UserProgramCourse'
import { ROUTE_TODAY } from 'components/common/helpers/Paths'
import {
  EVENT_ACTION_PAGEVIEW,
  EVENT_CATEGORY_PROGRAM,
  saveUserEvent
} from 'components/common/api/UserEvents'
import { getSavedUserAssessmentPlan } from 'components/common/api/Users'
import UserAssessmentModal from '../UserAssessmentModal'
import UserProgramPractice from './UserProgramPractice'
import StepsCounter from 'components/today/StepsCounter/StepsCounter'

/**
 * User Program component: used to display the user's program
 */
export default function UserProgram({ setHeaderTitle, setReturnPath }) {
  const { setUserProgram } = useContext(UserProgramContext)
  const { courses, progress } = useContext(CourseContext)
  const [program, setProgram] = useState()
  const [completed, setCompleted] = useState([])
  const [incomplete, setIncomplete] = useState([])
  const [isMissingAssessment, setIsMissingAssessment] = useState(false)

  // Once we have the user program, log the page view
  useEffect(() => {
    // Scroll to the top of the page
    window.scrollTo(0, 0)

    // Set the header title and return path
    setHeaderTitle('Your Well-being Plan')
    setReturnPath(ROUTE_TODAY)

    // Return early if we already have the user program
    if (program) return

    // Get the user program
    async function getUserProgram() {
      let userProgram = await getSavedUserAssessmentPlan()
      let userProgress = progress || []

      // If the program is an array, use the first item
      if (Array.isArray(userProgram)) userProgram = userProgram[0]

      const programCourses = userProgram?.courses?.split(',')
      let completedCourses = []
      let incomplete = []

      if (programCourses?.length > 0) {
        // Get the completed courses
        completedCourses = programCourses.filter(courseId =>
          userProgress.find(
            course =>
              course.course === parseInt(courseId) && course.date_completed
          )
        )

        // Now get the incomplete courses
        incomplete = programCourses.filter(
          courseId => !completedCourses.includes(courseId)
        )
      }

      setProgram(userProgram)
      setCompleted(completedCourses)
      setIncomplete(incomplete)

      // Update the user program in the context
      setUserProgram(userProgram)

      // If we don't have a program, set the missing assessment flag
      setIsMissingAssessment(!userProgram)
    }

    getUserProgram()

    // Log the page view
    saveUserEvent(EVENT_CATEGORY_PROGRAM, EVENT_ACTION_PAGEVIEW)

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [program, progress])

  return (
    <Container className='user-program-container'>
      {/* If the user is missing an assessment, show the modal */}
      {isMissingAssessment ? (
        <UserAssessmentModal className='my-4' />
      ) : (
        <Loading isLoaded={!program || !courses}>
          <Row className='d-flex align-items-center'>
            <Col xs={6} className='px-0'>
              <h4 className='mb-0 title'>Your Plan</h4>
            </Col>
            <Col xs={6} className='text-right px-0'>
              <UserAssessmentModal variant='link' linkText='change' />
            </Col>
          </Row>
          <Row>
            <Col xs={12} className='px-0'>
              <p>
                These are the courses and practices that will help you reach
                your goal. Complete the courses in order, and practice the
                skills you learn in your daily life.
              </p>
              <p className='muted small'>
                Complete the sessions within the course to unlock the next one.
              </p>
              <StepsCounter />
              <Row className='program-courses'>
                <Col>
                  <h4>Next on your Plan</h4>
                  {incomplete.map((courseId, courseIndex) => (
                    <UserProgramCourse
                      key={courseIndex}
                      courseId={courseId}
                      courseIndex={courseIndex}
                      className='my-2'
                    />
                  ))}
                </Col>
              </Row>
              <hr className='my-4' />

              {/* If the user doesn't have any incomplete courses, let's suggest they take the assessment */}
              {!incomplete.length && (
                <Row>
                  <Col xs={12} className='text-center my-5'>
                    <h2>Uh oh. Something went wrong.</h2>
                    <p className='muted'>
                      Unfortunately, we couldn't find any courses for you or
                      your program could not be loaded.
                    </p>
                  </Col>
                  <Col xs={6} className='text-center'>
                    <UserAssessmentModal
                      variant='link'
                      linkText='Retake assessment'
                    />{' '}
                  </Col>
                  <Col xs={6} className='text-center'>
                    <Button
                      variant='primary'
                      size='sm'
                      className='green text-decoration-none'
                      onClick={_ => window.location.reload()}
                    >
                      Reload program
                    </Button>
                  </Col>
                </Row>
              )}

              <Row className='program-practices'>
                <Col>
                  {program?.practices
                    ?.split(',')
                    .map((practiceKey, practiceIndex) => (
                      <UserProgramPractice
                        key={practiceIndex}
                        type={practiceKey}
                        practiceIndex={practiceIndex}
                        className='my-2'
                      />
                    ))}
                </Col>
              </Row>

              {/* Do we have any completed courses? */}
              {completed.length ? (
                <Row className='program-courses'>
                  <Col>
                    <h3 className='my-4'>Completed Courses</h3>
                    {completed.map((courseId, courseIndex) => (
                      <UserProgramCourse
                        key={courseIndex}
                        courseId={courseId}
                        courseIndex={courseIndex}
                        className='my-2 completed'
                      />
                    ))}
                  </Col>
                </Row>
              ) : null}
            </Col>
          </Row>
        </Loading>
      )}
    </Container>
  )
}
